import { useEffect, useRef, useState } from "react";
import { ThumbsUp } from "lucide-react";
import shopApi from "../../../../api/shopApi";
import { useNotification } from "../../../../hooks/useNotification";
import { reactions } from "../reactionConfig.js";

const CLOSE_DELAY_MS = 150;

export { reactions };

export function ReactionPopup({ shop, msg, isGroup, onReactionApplied }) {
  const { addNotification } = useNotification();

  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  const closeTimerRef = useRef(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
  };

  const openNow = () => {
    clearCloseTimer();
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clearCloseTimer();
    };
  }, [isOpen]);

  const handleReactMessage = (rIcon, rType) => {
    setIsOpen(false);
    const payload = {
      shopId: shop._id,
      id: msg.messageId,
      msgId: msg.msgId,
      cliMsgId: msg.cliMsgId,
      type: msg.type,
      rIcon,
      rType,
    };
    const apiCall = isGroup
      ? shopApi.reactMessageGroup(payload)
      : shopApi.reactMessage(payload);
    apiCall.then((res) => {
      if (res.status !== 1) {
        addNotification(res.message, res.status);
        return;
      }

      onReactionApplied?.({
        uidFrom: "0",
        idTo: msg.messageId,
        msgId: msg.msgId,
        cliMsgId: msg.cliMsgId,
        rType,
        rIcon,
        ts: Date.now(),
      });
    });
  };

  return (
    <div
      ref={rootRef}
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className="flex items-center justify-center h-6 w-6 rounded-full bg-white text-gray-500 shadow-sm ring-1 ring-black/5 hover:bg-gray-50 hover:text-gray-700 active:bg-gray-100 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-blue-400/40 focus-visible:outline-none"
        onClick={(e) => {
          e.stopPropagation();
          handleReactMessage("/-strong", 3);
        }}
        aria-label="Thả cảm xúc"
        title="Thả cảm xúc"
      >
        <ThumbsUp className="h-3 w-3" />
      </button>
      {isOpen && (
        <div
          className={`absolute bottom-full ${
            msg.uidFrom === "0" ? "right-0" : "left-0"
          } mb-1 z-50 bg-white rounded-full shadow-lg ring-1 ring-gray-200 flex items-center gap-0.5 px-1 py-1 before:content-[''] before:absolute before:-bottom-2 before:inset-x-0 before:h-2`}
          onMouseEnter={openNow}
          onMouseLeave={scheduleClose}
          role="menu"
          aria-label="Chọn cảm xúc"
        >
          {reactions.map((reaction) => (
            <button
              key={reaction.name}
              type="button"
              className="text-[16px] leading-none w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 hover:scale-125 transition-transform cursor-pointer focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:outline-none"
              onClick={(e) => {
                e.stopPropagation();
                handleReactMessage(reaction.rIcon, reaction.rType);
              }}
              title={reaction.name}
              aria-label={reaction.name}
            >
              {reaction.icon}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
